<template>
  <VApp
    :class="{ 'pwa-controls-visible': pwaControlsVisible }"
  >
    <!-- Starts the cart request that the cart readers wait for (useCartStore in stores/cart.ts). -->
    <BaseLayoutCart :nav-cart="navCart" />
    <a href="#main-content" class="skip-nav" @click="cleanHash">{{ t('nav.btn.skipToMainContent') }}</a>
    <SLoadingOverlay :loading="loading" class="global-overlay" />
    <ClientOnly>
      <PwaControlBar v-if="pwaControlsVisible" />
      <PwaReloadProgress v-if="pwaControlsVisible" />
    </ClientOnly>
    <!-- Add-to-home-screen prompt, homepage only, as in nuxt2. AB#64459 -->
    <PwaPrompt v-if="homepage" />
    <!-- Decides itself whether it shows: that depends on the fetched cart. -->
    <LayoutHeaderSharedCartHeader />
    <AgentHeader class="d-print-none" />
    <!-- The proxy-order checkout's Back and the subscription checkout's Exit sit
         in the teal bar until the page scrolls, and in the collapsed header after
         (HeaderLayout), as in nuxt2. Pinned in the header row at rest, they
         landed on the desktop logo.
         The slot itself is conditional, not just its content: Vue 3 passes a
         slot whose only content is a v-if comment (Vue 2 dropped it), and
         HeaderDesktop reads a passed slot as "replace the promo". This comment
         sits out here because inside the tag, beside the explicit #default, it
         would count as implicit slot content in dev. -->
    <LazyVisibleLayoutHeaderDesktop hydrate-on-visible :empty="emptyHeader" :display-demo="displayDemo" :is-demo="isDemo" :is-subscription="isSubscription" class="d-print-none">
      <template v-if="isProxyOrderCheckout" #default>
        <BaseBackNav class="text-white px-3" @click="emit('back')" />
      </template>
      <template v-else-if="isSubscription" #default>
        <LazyLayoutHeaderSubscriptionExit class="px-3 pt-1" data-testid="subscription-exit" />
      </template>
    </LazyVisibleLayoutHeaderDesktop>
    <!--
      need v-if to prevent multiple fetches
      need class to prevent initial flash before vuetify is set
     -->
    <HeaderLayout
      :links="navLinks"
      :user-menu="userMenu"
      :search="navSearch"
      :wishlist="navWishlist"
      :loyalty="navLoyalty"
      :events="navEvents"
      :cart="navCart"
      :is-subscription="isSubscription"
      :is-proxy-order-checkout="isProxyOrderCheckout"
      :static-header="staticHeader"
      class="d-print-none"
      @back="emit('back')"
    />

    <LazyVisibleAccountDemonstratorMobileDemoHighlight hydrate-on-visible class="hidden-md-and-up" />
    <AppNotificationProvider />
    <VMain
      id="main-content"
      tabindex="-1"
      class="site-content new-nav"
      :class="[{ 'background-secondary': dark, 'global-loading': loading }, { 'account-shell': route.path.startsWith('/account') }, { 'site-content--flush-footer': hideFooterTagline }]"
    >
      <LazyLayoutHeaderPromotionalRibbon
        v-if="promotionRibbon"
        class="ribbon"
      />
      <slot><NuxtPage /></slot>
      <LoyaltyOptInDialog />
      <LazyVisibleCartFailureDialog hydrate-on-visible />
    </VMain>
    <LazyLayoutFooterAppFooter v-if="footer" :hide-tagline="hideFooterTagline" class="d-print-none" />
    <LazyBaseAppChat v-if="chatVisible" data-testid="app-chat" />
  </VApp>
</template>

<script lang="ts" setup>
import PwaPrompt from '~/components/pwa/PwaPrompt.vue'
import AgentHeader from '~/components/agent/AgentHeader.vue'
import PwaControlBar from '~/components/pwa/PwaControlBar.vue'
import PwaReloadProgress from '~/components/pwa/PwaReloadProgress.vue'
// import SharedCartHeader from '~/components/layout/header/SharedCartHeader.vue'
import LoyaltyOptInDialog from '~/components/account/rewards/RewardsOptInDialog.vue'
import { useRootStore } from '~/stores/root'
import { useShopStore } from '~/stores/shop'
import HeaderLayout from "~/components/layout/header/HeaderLayout.vue";
import BaseLayoutCart from '~/components/layout/BaseLayoutCart.vue'
import AppNotificationProvider from '~/components/notification/NotificationProvider.vue'
import { isStandalonePwa, PWA_BROWSER_THEME_COLOR, PWA_STANDALONE_THEME_META } from '~/utils/pwa'
import { cleanI18nMeta } from '~/utils/locale'
import { useLocaleStore } from '~/stores/locale'
import { UserRole, type User } from '~/types/account'
import { pushUserSegmentation } from '~/utils/gtm'

interface Props {
 footer?: boolean
 hideChat?: boolean
 promotionRibbon?: boolean
 promotionBanner?: boolean
 navCart?: boolean
 navSearch?: boolean
 navWishlist?: boolean
 navLoyalty?: boolean
 navEvents?: boolean
 navLinks?: boolean
 displayDemo?: boolean
 emptyHeader?: boolean
 isSubscription?: boolean
 isProxyOrderCheckout?: boolean
 checkout?: boolean
 dark?: boolean
 noCanonical?: boolean
 userMenu?: boolean
 staticHeader?: boolean
 hideFooterTagline?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  footer: true,
  hideChat: false,
  promotionRibbon: false,
  promotionBanner: false,
  navCart: true,
  navSearch: true,
  isSubscription: false,
  isProxyOrderCheckout: false,
  navWishlist: true,
  navLoyalty: true,
  navEvents: true,
  navLinks: true,
  displayDemo: false,
  emptyHeader: false,
  checkout: false,
  dark: false,
  noCanonical: false,
  userMenu: true,
  staticHeader: false,
  hideFooterTagline: false
})

const emit = defineEmits<{
  (e: 'back'): void
}>()

// `navIsOpen` and its `nav-state-changed` plumbing were removed with AB#63271. They existed
// only to feed the `no--search` / `position-fixed` VApp bindings, which the parity plan
// (Wave 9) records as deliberately skipped: they are dead in Nuxt2 as well, nothing ever
// emitted the event, and MobileMenu already locks background scroll via useScrollLock.
const pwaControlsVisible = ref(false)

const { t } = useI18n()
const { cleanHash } = useSkipNav()

const { isDemo, isAgentOrImpersonating, user } = useSAuthState()
const rootStore = useRootStore()

const loading = computed(() => rootStore.rootLoading)

const shopStore = useShopStore()
const localeStore = useLocaleStore()
const route = useRoute()
const homepage = computed(() => isHomepage(route.name))

// Chat reaches customers AND demonstrators (product-confirmed), never an agent or an impersonating
// agent, and never where the page opts out. Nuxt2 gated on the same two roles.
const chatVisible = computed(() => {
  const role = user.value?.role
  return !!role && [UserRole.Customer, UserRole.Demonstrator].includes(role) &&
    !props.hideChat && !isAgentOrImpersonating.value
})

// AB#62456 AC2: the PWA control bar overlaps the Talkdesk launcher, which only renders for an
// authenticated Customer/Demonstrator and is only offered in North America. Mirror the chat's own
// condition so we never offset a widget that isn't there.
const chatOffsetActive = computed(() =>
  pwaControlsVisible.value && chatVisible.value && localeStore.isNaCountry)

// i18n SEO head (AB#63271). nuxt-i18n-micro's meta plugin already emits lang, canonical and
// og:locale on every layout; this layers Nuxt2's cleanI18nMeta on top — dir=ltr, no
// og:locale:alternate, and no canonical when the page owns its own (noCanonical). No hreflang:
// Nuxt2 ran nuxt-i18n with strategy no_prefix, which never emits alternates, and neither does
// i18n-micro under no_prefix.
// Path only, no query: Nuxt2's addCanonicalLinks built the href from `currentRoute.path` alone.
// Absolute rather than Nuxt2's relative href, which was an accident of an empty baseUrl at plugin
// init. og:url is pinned to the same value so the two always agree.
// Origin from the culture's configured domain — the baseUrl product canonicals already use —
// not the request: X-Forwarded-Host is client-supplied unless the edge overwrites it, and these
// are the two tags a spoofed host is worth something in. Origin only, because route.path already
// carries the language segment (/fr) that baseUrl ends with.
const canonicalUrl = computed(() =>
  localeStore.baseUrl ? `${new URL(localeStore.baseUrl).origin}${route.path}` : undefined)
const i18nSeoHead = computed(() => cleanI18nMeta(canonicalUrl.value, props.noCanonical))
const { pageHead, resetPageHead } = useI18nHead(i18nSeoHead)
// The override is app-wide state, so drop it when this layout goes away (join / barebone routes)
// — unless the next page's layout has already replaced it with its own.
onBeforeUnmount(() => {
  if (toRaw(pageHead.value) === i18nSeoHead.value) {
    resetPageHead()
  }
})

// The standalone-PWA theme colour goes first: the first theme-color whose media matches wins, so
// the unconditional browser colour has to follow it. The sitename JSON-LD is skipped where the
// translation is empty (every culture but en-us today) instead of emitting an empty ld+json block.
const sitenameJsonLd = t('seo.sitename')
useServerHead(() => ({
  meta: [
    PWA_STANDALONE_THEME_META,
    { key: 'theme-color', name: 'theme-color', content: PWA_BROWSER_THEME_COLOR }
  ],
  script: sitenameJsonLd && sitenameJsonLd !== 'seo.sitename'
    ? [{ key: 'sitename-json-ld', type: 'application/ld+json', innerHTML: sitenameJsonLd }]
    : []
}))

// Push account segmentation to the dataLayer for UserPilot on each authenticated page load
// (AB#60396). Fires for any real account (Customer/Demonstrator/Recruit/Guest) but not for
// anonymous visitors. Values are sent raw from the account; UserPilot maps them. Matches
// Nuxt2 BaseLayout's onMounted call.
const pushSegmentation = () => {
  const role = user.value?.role
  if (role && role !== UserRole.Anonymous) {
    const { proxy: gtm } = useScriptGoogleTagManager()
    pushUserSegmentation(gtm, user.value as User)
  }
}

onMounted(() => {
  pushSegmentation()
  pwaControlsVisible.value = isStandalonePwa()
})

// The control-bar custom properties have to live on <body>: the Talkdesk launcher is injected
// outside the app root, so it cannot inherit them from VApp (AB#62456 AC2). Drive the class
// reactively so it follows PWA state rather than a one-shot poke, and clear it on unmount.
if (import.meta.client) {
  watchEffect(() => {
    document.body.classList.toggle('pwa-controls', pwaControlsVisible.value)
    document.body.classList.toggle('pwa-chat-offset', chatOffsetActive.value)
  })

  onBeforeUnmount(() => {
    document.body.classList.remove('pwa-controls')
    document.body.classList.remove('pwa-chat-offset')
  })
}

// "More to explore" feeds the recommendations in the cart drawer and flyout
// (CartNotification), which any page with the header cart can open. Fetched in the
// browser once the app is idle, not in the SSR pass as nuxt2 did, where it cost
// every page a slow backend call and ~39KB of payload. The store keeps the result
// and shares an in-flight request with the cart page's own fetch.
if (props.navCart) {
  onNuxtReady(() => {
    // Optional content: on failure the drawer leaves the block out, and the cart
    // page fetches again on mount.
    shopStore.fetchMoreToExplore().catch(() => {})
  })
}
</script>

<style lang="scss">
@use '~/assets/scss/settings';
@use 'sass:map';

// Defined on <body> rather than on VApp so the Talkdesk launcher — injected outside the app root —
// can read them too (AB#62456 AC2). Custom properties inherit, so the app-tree rules in the scoped
// block below still resolve these normally.
body.pwa-controls {
  --pwa-control-bar-height: 64px;
  // Clear the iPhone home indicator: scale with the safe-area inset, less a 10px trim (the full
  // Face ID inset read too tall on device, AB#62598), never below a 2px floor so non-notched
  // devices still get baseline clearance (AB#62456 AC1).
  --pwa-safe-bottom: max(calc(env(safe-area-inset-bottom, 0px) - 10px), 2px);
  --pwa-control-bar-offset: calc(var(--pwa-control-bar-height) + var(--pwa-safe-bottom));

  // Backstop for the failure this feature already hit on Nuxt2 (AB#62684, AB#62690): any
  // horizontal overflow makes the browser expand the mobile layout viewport, so window
  // .innerHeight grows past the real viewable height and the bottom-fixed control bar lands
  // below the fold. viewport-fit=cover (nuxt.config.ts) does not cause it, but it is what makes
  // a pre-existing overflow visibly clip the bar. Nuxt2's offenders were Vuetify negative
  // margins spilling past a low-padding container. A sweep of `mx-n*`/`ml-n*`/`mr-n*` here
  // found three wider than mobile container padding: `mx-n12` in
  // components/portal/events/Calendar.vue, `mx-n10` on the two VDividers in
  // pages/account/rewards/rewards-gifting.vue, and `mx-n9` in
  // pages/account/demonstrator/index.vue. None is fixed here -- each needs its own
  // container-padding check on a real device, and this backstop is what keeps a missed
  // one from dropping the bar below the fold in the meantime.
  // `clip` not `hidden`: hidden would make <body> a scroll container and break the fixed
  // positioning this bar depends on. Scoped to PWA mode, so the browser experience is untouched.
  overflow-x: clip;

  // The launcher renders at bottom: 20px; lift it clear of the bar. Selector confirmed on-device
  // during the Nuxt2 work (2026-08-18) — the older .digital-webchat-wrapper class is gone.
  // Scoped to md-and-down: above that the bar is pinned to the top and the launcher is already
  // clear, so leave the SDK's own resting position alone rather than override it back.
  // Gated on pwa-chat-offset, which BaseLayout sets only when the launcher is actually present —
  // an authenticated Customer/Demonstrator in North America — matching Nuxt2 (AB#62456 AC2)
  // rather than offsetting for every PWA session.
  &.pwa-chat-offset {
    @media #{map.get(settings.$display-breakpoints, 'md-and-down')} {
      .tdw-widget,
      .tdw-widget-trigger {
        bottom: calc(var(--pwa-control-bar-offset) + 16px) !important;
      }
    }
  }

  @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
    // Bar is pinned to the top on desktop, so drop the home-indicator clearance entirely.
    --pwa-safe-bottom: 0px;
    --pwa-control-bar-offset: 0px;
  }
}
</style>

<style lang="scss" scoped>
@use '~/assets/scss/settings';
@use 'sass:map';

.global-overlay {
  z-index: 2147483738;
}
.global-overlay :deep(.v-progress-circular) {
  position: absolute;
  top: 50vh;
}
.global-loading :deep(.demo-homepage),
.global-loading :deep(.loading-overlay) {
  display: none;
}
.ribbon {
  position: relative;
  z-index: 1;
}

// --pwa-control-bar-height / --pwa-safe-bottom / --pwa-control-bar-offset are defined once on
// body.pwa-controls (unscoped block above) and inherit down to here.
.pwa-controls-visible {
  :deep(.v-application__wrap) {
    padding-bottom: var(--pwa-control-bar-offset);
  }

  :deep(.pwa-offset-footer) {
    bottom: var(--pwa-control-bar-offset) !important;
    z-index: 202 !important;
  }

  @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
    :deep(.v-application__wrap) {
      padding-top: var(--pwa-control-bar-height);
      padding-bottom: 0;
    }

    // A static header (SparkIt, AB#62164) scrolls with the document, so offsetting it below the
    // control bar would just push it down.
    :deep(.nav-component:not(.nav-component--static)) {
      top: var(--pwa-control-bar-height);
    }

    :deep(.pwa-offset-footer) {
      bottom: 0 !important;
      z-index: initial !important;
    }
  }
}
</style>
